import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { isNil } from 'lodash'
import { PaymentAssignmentStatusId } from '../Utils/types'
import type { BenefitEventPaMetadata } from '../../../api/dto/set-payment-assignment-status.dto'
import { PaymentTypeEnum } from 'types'

dayjs.extend(customParseFormat)

export enum StatusUpdateFormKey {
  ASSIGNEE_TO_ID = 'assigneeId',
  ESCALATOR_TO_ID = 'escalatorId',
  STATUS = 'status',
  PRIORITY = 'priority',
  NOTE = 'note',
  // Payment Submitted
  ACTUAL_PREMIUM_PAID = 'actualPremiumPaid',
  ACTUAL_PAYMENT_DATE = 'actualPaymentDate',
  CONFIRMATION_CODE = 'confirmationCode',
  DID_SET_UP_AUTOPAY = 'didSetUpAutopay',
  AUTOPAY_PAYMENT_METHOD = 'autopayPaymentMethod',
  AUTOPAY_PULL_DAY = 'autopayPullDay',
  CHECK_NUMBER = 'checkNumber',
  PAYMENT_SUBMITTED_TX_ID = 'paymentSubmittedTxId',
  // Paid Escalation
  ESCALATE_REASON = 'escalateReason',
  ESCALATE_EXPLANATION = 'escalateExplanation',
  // Terminated EE
  UPDATE_BENEFIT_END_DATE = 'updateBenefitEndDate',
  UPDATE_EFFECTIVE_DATE = 'updateEffectiveDate',
  TICKET = 'ticket',
  EE_CONTACT = 'eeContact',
  HR_CONTACT = 'hrContact',
  // Fully Resolved
  DID_MAKE_PAYMENT = 'didMakePayment',
  // Payment Bot
  PAYMENT_BOT_TASK_ID = 'paymentBotTaskId',
  PAYMENT_BOT_TASK_TYPE = 'paymentBotTaskType',
  PG_BOSS_JOB_ID = 'pgBossJobId',
  PAYMENT_BOT_VOICE_RECORDING_URL = 'paymentBotVoiceRecordingUrl',
  // Clear
  CLEARED_COST = 'clearedCost',
  CLEARED_EXPECTED_COST = 'clearedExpectedCost',
  CLEARED_STATUS = 'clearedStatus',
  // QLE Migration
  MOVED_FROM_BENEFIT_ID = 'movedFromBenefitId',
  // Additional Note
  ADDITIONAL_NOTE = 'additionalNote',
}

export const StatusUpdateFormKeyLabels = {
  [StatusUpdateFormKey.ASSIGNEE_TO_ID]: 'Assign To',
  [StatusUpdateFormKey.ESCALATOR_TO_ID]: 'Escalator To',
  [StatusUpdateFormKey.STATUS]: 'Status',
  [StatusUpdateFormKey.PRIORITY]: 'Priority',
  [StatusUpdateFormKey.NOTE]: 'Note',
  [StatusUpdateFormKey.ACTUAL_PREMIUM_PAID]: 'Actual Premium Paid',
  [StatusUpdateFormKey.ACTUAL_PAYMENT_DATE]: 'Actual Payment Date',
  [StatusUpdateFormKey.CONFIRMATION_CODE]: 'Confirmation Code',
  [StatusUpdateFormKey.CHECK_NUMBER]: 'Check Number',
  [StatusUpdateFormKey.DID_SET_UP_AUTOPAY]: 'Did you set up Autopay?',
  [StatusUpdateFormKey.AUTOPAY_PAYMENT_METHOD]: 'Payment Method',
  [StatusUpdateFormKey.AUTOPAY_PULL_DAY]: 'Autopay Pull Day',
  [StatusUpdateFormKey.ESCALATE_REASON]: 'Escalation Reason',
  [StatusUpdateFormKey.ESCALATE_EXPLANATION]: 'Escalate Explanation',
  [StatusUpdateFormKey.UPDATE_BENEFIT_END_DATE]: 'Benefit End Date',
  [StatusUpdateFormKey.UPDATE_EFFECTIVE_DATE]: 'New Effective Date',
  [StatusUpdateFormKey.TICKET]: 'Ticket',
  [StatusUpdateFormKey.EE_CONTACT]: 'EE Contact',
  [StatusUpdateFormKey.HR_CONTACT]: 'HR Contact',
  [StatusUpdateFormKey.DID_MAKE_PAYMENT]: 'Did you make payment to resolve?',
  [StatusUpdateFormKey.PAYMENT_BOT_TASK_ID]: 'Payment Bot Task ID',
  [StatusUpdateFormKey.PAYMENT_BOT_TASK_TYPE]: 'Payment Bot Task Type',
  [StatusUpdateFormKey.PG_BOSS_JOB_ID]: 'PG Boss Job ID',
  [StatusUpdateFormKey.PAYMENT_BOT_VOICE_RECORDING_URL]: 'IVR recording',
  [StatusUpdateFormKey.CLEARED_COST]: 'Cleared Cost',
  [StatusUpdateFormKey.CLEARED_EXPECTED_COST]: 'Cleared Expected Cost',
  [StatusUpdateFormKey.CLEARED_STATUS]: 'Cleared Status',
  [StatusUpdateFormKey.MOVED_FROM_BENEFIT_ID]: 'Moved From Benefit ID',
}

// ---------------------------------------------------------------------------
// Status → field configuration
//
// Single source of truth describing, per status, which status-specific
// metadata fields apply and whether they are required. The metadata builder
// and per-row validators below are derived from this so that CaseManagement
// and the bulk-status page stay in sync.
// ---------------------------------------------------------------------------

export type StatusFieldValueType =
  | 'number'
  | 'string'
  | 'boolean'
  | 'date'
  | 'day'

export interface StatusFieldRule {
  key: StatusUpdateFormKey
  required: boolean
  type: StatusFieldValueType
  /**
   * Discrete allowed values for this field (drives a Select in both
   * CaseManagement and the bulk-status table). When absent the field accepts
   * free-form input.
   */
  options?: readonly string[]
  /**
   * Only applies (validated / built) when this predicate over the row values
   * returns true. Used for autopay + didMakePayment conditional fields.
   */
  when?: (values: Record<string, unknown>) => boolean
}

export function normalizeNullableId(v: unknown): number | null {
  if (v === undefined || v === null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

// ---------------------------------------------------------------------------
// Shared select option constants (exported so UI components can import them)
// ---------------------------------------------------------------------------

export const ESCALATION_REASON_OPTIONS = [
  '$0 Balance/Minimal Balance',
  'EE Cancelled Plan',
  'Live Rep Call',
  'Member Not Found',
  'Need EE to assist',
  'Need Member ID/Member ID',
  'Need More Info',
  'Payment Issue',
  'Premium Discrepancy',
  'Other',
] as const

/** Subset of escalation reasons used specifically for Paid Escalation. */
export const PAID_ESCALATION_REASON_OPTIONS = [
  'Premium Discrepancy',
  'Other',
] as const

export const AUTOPAY_METHOD_OPTIONS = ['VCN', 'ACH'] as const

export const AUTOPAY_PULL_DAY_OPTIONS = Array.from({ length: 31 }, (_, i) =>
  String(i + 1)
) as string[]

export const BOOLEAN_OPTIONS = ['Yes', 'No'] as const

const isAutopayEnabled = (values: Record<string, unknown>): boolean =>
  toBoolean(values[StatusUpdateFormKey.DID_SET_UP_AUTOPAY])

const didMakePayment = (values: Record<string, unknown>): boolean =>
  toBoolean(values[StatusUpdateFormKey.DID_MAKE_PAYMENT])

/** Payment-evidence rules shared by PAYMENT_SUBMITTED / PAID_ESCALATION / resolved-with-payment. */
const PAYMENT_EVIDENCE_RULES: StatusFieldRule[] = [
  {
    key: StatusUpdateFormKey.ACTUAL_PREMIUM_PAID,
    required: true,
    type: 'number',
  },
  {
    key: StatusUpdateFormKey.ACTUAL_PAYMENT_DATE,
    required: true,
    type: 'date',
  },
  {
    key: StatusUpdateFormKey.CONFIRMATION_CODE,
    required: false,
    type: 'string',
  },
  {
    key: StatusUpdateFormKey.CHECK_NUMBER,
    required: false,
    type: 'string',
  },
  {
    key: StatusUpdateFormKey.DID_SET_UP_AUTOPAY,
    required: false,
    type: 'boolean',
    options: BOOLEAN_OPTIONS,
  },
  {
    key: StatusUpdateFormKey.AUTOPAY_PAYMENT_METHOD,
    required: true,
    type: 'string',
    options: AUTOPAY_METHOD_OPTIONS,
    when: isAutopayEnabled,
  },
  {
    key: StatusUpdateFormKey.AUTOPAY_PULL_DAY,
    required: true,
    type: 'day',
    options: AUTOPAY_PULL_DAY_OPTIONS,
    when: isAutopayEnabled,
  },
]

const ESCALATION_RULES: StatusFieldRule[] = [
  { key: StatusUpdateFormKey.TICKET, required: true, type: 'string' },
  {
    key: StatusUpdateFormKey.ESCALATE_REASON,
    required: true,
    type: 'string',
    options: ESCALATION_REASON_OPTIONS,
  },
  {
    key: StatusUpdateFormKey.ESCALATE_EXPLANATION,
    required: false,
    type: 'string',
  },
]

/** Escalation rules with the narrower reason list used for Paid Escalation. */
const PAID_ESCALATION_RULES: StatusFieldRule[] = [
  { key: StatusUpdateFormKey.TICKET, required: true, type: 'string' },
  {
    key: StatusUpdateFormKey.ESCALATE_REASON,
    required: true,
    type: 'string',
    options: PAID_ESCALATION_REASON_OPTIONS,
  },
  {
    key: StatusUpdateFormKey.ESCALATE_EXPLANATION,
    required: false,
    type: 'string',
  },
]

const CONTACT_RESOLUTION_RULES: StatusFieldRule[] = [
  { key: StatusUpdateFormKey.TICKET, required: true, type: 'string' },
  {
    key: StatusUpdateFormKey.EE_CONTACT,
    required: false,
    type: 'boolean',
    options: BOOLEAN_OPTIONS,
  },
  {
    key: StatusUpdateFormKey.HR_CONTACT,
    required: false,
    type: 'boolean',
    options: BOOLEAN_OPTIONS,
  },
]

/**
 * Status → status-specific field rules. Represents the superset of fields that
 * may be submitted for a given status (Checker interpretation, ticket-based).
 *
 * Statuses with no special fields (e.g. PENDING_ASSIGNMENT, PAYMENT_CLEARED)
 * are listed with an empty array so they appear in STATUSES_WITH_FIELD_CONFIG
 * and can be selected on the bulk-update page without additional inputs.
 */
export const STATUS_FIELD_CONFIG: Partial<
  Record<PaymentAssignmentStatusId, StatusFieldRule[]>
> = {
  // ---- statuses with no additional fields -----------------------------------
  [PaymentAssignmentStatusId.PENDING_ASSIGNMENT]: [],
  [PaymentAssignmentStatusId.PENDING_PAYMENT]: [],
  [PaymentAssignmentStatusId.PAYMENT_CLEARED]: [],
  [PaymentAssignmentStatusId.AP_CONFIRMED]: [],
  [PaymentAssignmentStatusId.PENDING_AUTOPAY]: [],
  [PaymentAssignmentStatusId.REATTEMPT]: [],
  [PaymentAssignmentStatusId.BOT_CLEARED]: [],

  // ---- statuses with field inputs -------------------------------------------
  [PaymentAssignmentStatusId.PAYMENT_SUBMITTED]: PAYMENT_EVIDENCE_RULES,

  [PaymentAssignmentStatusId.PAID_ESCALATION]: [
    ...PAID_ESCALATION_RULES,
    ...PAYMENT_EVIDENCE_RULES,
  ],

  [PaymentAssignmentStatusId.UNPAID_ESCALATION]: ESCALATION_RULES,

  [PaymentAssignmentStatusId.ESCALATION_FULLY_RESOLVED]: [
    ...CONTACT_RESOLUTION_RULES,
    {
      key: StatusUpdateFormKey.DID_MAKE_PAYMENT,
      required: false,
      type: 'boolean',
      options: BOOLEAN_OPTIONS,
    },
    ...PAYMENT_EVIDENCE_RULES.map((rule) => ({
      ...rule,
      when: (values: Record<string, unknown>) =>
        didMakePayment(values) && (rule.when ? rule.when(values) : true),
    })),
  ],

  [PaymentAssignmentStatusId.EE_PAID]: CONTACT_RESOLUTION_RULES,
  [PaymentAssignmentStatusId.TERMINATED_EE]: CONTACT_RESOLUTION_RULES,

  [PaymentAssignmentStatusId.TERMINATED_PLAN]: [
    ...CONTACT_RESOLUTION_RULES,
    {
      key: StatusUpdateFormKey.UPDATE_BENEFIT_END_DATE,
      required: true,
      type: 'date',
    },
  ],

  [PaymentAssignmentStatusId.NEW_EFFECTIVE_DATE]: CONTACT_RESOLUTION_RULES,
  [PaymentAssignmentStatusId.CARRIER_SHOW_PAYMENT]: CONTACT_RESOLUTION_RULES,
}

/** Statuses that expose status-specific fields via STATUS_FIELD_CONFIG. */
export const STATUSES_WITH_FIELD_CONFIG = Object.keys(
  STATUS_FIELD_CONFIG
) as PaymentAssignmentStatusId[]

// ---------------------------------------------------------------------------
// Normalization helpers (ported from CaseManagementSection.handleFormSubmit)
// ---------------------------------------------------------------------------

const toDateString = (v: unknown): string | undefined => {
  if (!v) return undefined
  if (dayjs.isDayjs(v)) return v.format('YYYY-MM-DD')
  const d = dayjs(v as dayjs.ConfigType, ['MM-DD-YYYY', 'YYYY-MM-DD'])
  return d.isValid() ? d.format('YYYY-MM-DD') : undefined
}

/**
 * Case-insensitive boolean coercion — CSV uploads may contain "Yes", "yes",
 * "YES", "YEs", etc. and they should all be treated as true.
 */
const toBoolean = (v: unknown): boolean => {
  if (v === true) return true
  if (typeof v !== 'string') return false
  const normalized = v.trim().toLowerCase()
  return normalized === 'true' || normalized === 'yes'
}

/**
 * Builds the metadata payload for a status update from raw form/row values.
 * Behavior mirrors CaseManagementSection.handleFormSubmit exactly.
 *
 * `priority` is handled separately by CaseManagement (Checker only) and can be
 * passed via opts.priority.
 * `primaryPaymentMethod` is used to determine if autopay fields should be included.
 */
export function buildStatusMetadata(
  status: PaymentAssignmentStatusId | undefined,
  values: Record<string, unknown>,
  opts?: { priority?: unknown; primaryPaymentMethod?: string }
): BenefitEventPaMetadata | undefined {
  if (!status) return undefined

  const metadata: Record<string, unknown> = {}

  // Check if payment method is CHECK or GROUP_ACH - autopay is not applicable
  const isCheckOrGroupAch =
    opts?.primaryPaymentMethod === PaymentTypeEnum.CHECK ||
    opts?.primaryPaymentMethod === PaymentTypeEnum.GROUP_ACH

  const priorityValue = opts?.priority
  if (
    priorityValue !== undefined &&
    priorityValue !== null &&
    priorityValue !== ''
  ) {
    const priorityNum = Number(priorityValue)
    if (Number.isFinite(priorityNum)) {
      metadata.priority = priorityNum
    }
  }

  const hasPaymentEvidenceFields =
    status === PaymentAssignmentStatusId.PAYMENT_SUBMITTED ||
    status === PaymentAssignmentStatusId.PAID_ESCALATION ||
    (status === PaymentAssignmentStatusId.ESCALATION_FULLY_RESOLVED &&
      didMakePayment(values))

  if (hasPaymentEvidenceFields) {
    const actualPremiumPaid = normalizeNullableId(
      values[StatusUpdateFormKey.ACTUAL_PREMIUM_PAID]
    )
    const confirmationCode = values[StatusUpdateFormKey.CONFIRMATION_CODE]
    const paymentMethod = values[StatusUpdateFormKey.AUTOPAY_PAYMENT_METHOD]

    const actualPaymentDate = toDateString(
      values[StatusUpdateFormKey.ACTUAL_PAYMENT_DATE]
    )

    if (!isNil(actualPremiumPaid)) {
      metadata[StatusUpdateFormKey.ACTUAL_PREMIUM_PAID] = actualPremiumPaid
    }

    if (actualPaymentDate) {
      metadata[StatusUpdateFormKey.ACTUAL_PAYMENT_DATE] = actualPaymentDate
    }

    if (!isNil(confirmationCode) && confirmationCode !== '') {
      metadata[StatusUpdateFormKey.CONFIRMATION_CODE] =
        String(confirmationCode).trim()
    }

    const checkNumber = values[StatusUpdateFormKey.CHECK_NUMBER]
    if (!isNil(checkNumber) && checkNumber !== '') {
      metadata[StatusUpdateFormKey.CHECK_NUMBER] = String(checkNumber).trim()
    }

    // For CHECK and GROUP_ACH, autopay is not applicable - default to false
    if (isCheckOrGroupAch) {
      metadata[StatusUpdateFormKey.DID_SET_UP_AUTOPAY] = false
    } else {
      const autopayValue = values[StatusUpdateFormKey.DID_SET_UP_AUTOPAY]
      // Default to false if not set (optional field for bulk status)
      const setUpAutopay =
        isNil(autopayValue) || autopayValue === ''
          ? false
          : toBoolean(autopayValue)
      metadata[StatusUpdateFormKey.DID_SET_UP_AUTOPAY] = setUpAutopay
      if (setUpAutopay) {
        if (!isNil(paymentMethod)) {
          metadata[StatusUpdateFormKey.AUTOPAY_PAYMENT_METHOD] =
            String(paymentMethod).trim()
        }
        const autopayPullDayRaw = values[StatusUpdateFormKey.AUTOPAY_PULL_DAY]
        const autopayPullDay = isNil(autopayPullDayRaw)
          ? null
          : Number(autopayPullDayRaw)
        if (
          autopayPullDay !== null &&
          Number.isInteger(autopayPullDay) &&
          autopayPullDay >= 1 &&
          autopayPullDay <= 31
        ) {
          metadata[StatusUpdateFormKey.AUTOPAY_PULL_DAY] = autopayPullDay
        }
      }
    }
  }

  if (
    [
      PaymentAssignmentStatusId.PAID_ESCALATION,
      PaymentAssignmentStatusId.UNPAID_ESCALATION,
    ].includes(status)
  ) {
    ;[
      StatusUpdateFormKey.ESCALATE_REASON,
      StatusUpdateFormKey.ESCALATE_EXPLANATION,
    ].forEach((field) => {
      const value = values[field]
      if (!isNil(value)) {
        metadata[field] = String(value).trim()
      }
    })
    const ticket = values[StatusUpdateFormKey.TICKET]
    if (!isNil(ticket)) {
      metadata[StatusUpdateFormKey.TICKET] = String(ticket).trim()
    }
  }

  if (
    [
      PaymentAssignmentStatusId.ESCALATION_FULLY_RESOLVED,
      PaymentAssignmentStatusId.EE_PAID,
      PaymentAssignmentStatusId.TERMINATED_EE,
      PaymentAssignmentStatusId.TERMINATED_PLAN,
      PaymentAssignmentStatusId.NEW_EFFECTIVE_DATE,
      PaymentAssignmentStatusId.CARRIER_SHOW_PAYMENT,
    ].includes(status)
  ) {
    const ticket = values[StatusUpdateFormKey.TICKET]
    if (!isNil(ticket)) {
      metadata[StatusUpdateFormKey.TICKET] = String(ticket).trim()
    }
    if (status === PaymentAssignmentStatusId.ESCALATION_FULLY_RESOLVED) {
      metadata[StatusUpdateFormKey.DID_MAKE_PAYMENT] = didMakePayment(values)
    }
    ;[StatusUpdateFormKey.EE_CONTACT, StatusUpdateFormKey.HR_CONTACT].forEach(
      (key) => {
        if (values[key] !== undefined && values[key] !== null) {
          metadata[key] = toBoolean(values[key])
        }
      }
    )
  }

  if (status === PaymentAssignmentStatusId.TERMINATED_PLAN) {
    metadata[StatusUpdateFormKey.UPDATE_BENEFIT_END_DATE] = toDateString(
      values[StatusUpdateFormKey.UPDATE_BENEFIT_END_DATE]
    )
  }

  return Object.keys(metadata).length
    ? (metadata as BenefitEventPaMetadata)
    : undefined
}

// ---------------------------------------------------------------------------
// Row validation (used by the bulk-status page)
// ---------------------------------------------------------------------------

const isEmpty = (v: unknown): boolean =>
  v === undefined || v === null || (typeof v === 'string' && v.trim() === '')

/**
 * Returns human-readable validation errors for a single status-update row,
 * derived from STATUS_FIELD_CONFIG.
 */
export function getStatusFieldErrors(
  status: PaymentAssignmentStatusId | undefined,
  values: Record<string, unknown>
): string[] {
  if (!status) return ['Status is required']
  const rules = STATUS_FIELD_CONFIG[status]
  if (!rules) return [`"${status}" is not a supported status`]

  const errors: string[] = []
  for (const rule of rules) {
    if (rule.when && !rule.when(values)) continue
    const value = values[rule.key]
    const label = StatusUpdateFormKeyLabels[rule.key]

    if (rule.required && isEmpty(value)) {
      errors.push(`${label} is required`)
      continue
    }
    if (isEmpty(value)) continue

    if (rule.type === 'number') {
      const n = Number(value)
      if (!Number.isFinite(n)) {
        errors.push(`${label} must be a number`)
      } else if (n < 0) {
        errors.push(`${label} must be a positive number`)
      }
    }
    if (rule.type === 'day') {
      const n = Number(value)
      if (!Number.isInteger(n) || n < 1 || n > 31) {
        errors.push(`${label} must be a day between 1 and 31`)
      }
    }
    if (rule.type === 'date' && !toDateString(value)) {
      errors.push(`${label} must be a valid date (MM-DD-YYYY)`)
    }
    if (
      rule.options &&
      rule.options.length > 0 &&
      !rule.options.some(
        (o) => o.toLowerCase() === String(value).trim().toLowerCase()
      )
    ) {
      errors.push(`${label} must be one of: ${rule.options.join(', ')}`)
    }
  }
  return errors
}
